/**
 * Type for teacher-feedback Zoom user enrollment documents stored in Firestore at:
 * teacherFeedback/data/courses/{courseId}/zoomUsers/{zoomUserId}
 *
 * Each document represents a Zoom user's enrollment in the teacher-feedback app
 * for a specific course. The document ID is the Zoom user ID.
 */

import {z} from 'zod';

// ============================================================================
// Document Schema
// ============================================================================

/**
 * Schema for a Zoom user's enrollment status within a specific course in the
 * teacher-feedback app. Written by admins via the UI; read by the callable to
 * enrich Zoom API user data with app-level enrollment state.
 */
export const TeacherFeedbackZoomUserSchema = z.object({
  /**
   * Whether this Zoom user is enrolled in the teacher-feedback application
   * for this course.
   */
  enrolled: z.boolean(),
  /**
   * Zoom account email saved when enrollment is updated. Optional so existing
   * documents written before this field existed still parse.
   */
  email: z.string().optional(),
});

/** A Zoom user's enrollment record for a specific course. */
export type TeacherFeedbackZoomUser = z.infer<
  typeof TeacherFeedbackZoomUserSchema
>;

// ============================================================================
// Paths
// ============================================================================

/**
 * Returns the path to a course's Zoom users enrollment subcollection.
 * Path: teacherFeedback/data/courses/{courseId}/zoomUsers
 */
export function getTeacherFeedbackZoomUsersCollectionPath({
  courseId,
}: {
  courseId: string;
}): string {
  return `teacherFeedback/data/courses/${courseId}/zoomUsers`;
}

/**
 * Returns the path to a single Zoom user's enrollment document for a course.
 * Path: teacherFeedback/data/courses/{courseId}/zoomUsers/{zoomUserId}
 */
export function getTeacherFeedbackZoomUserDocumentPath({
  courseId,
  zoomUserId,
}: {
  courseId: string;
  zoomUserId: string;
}): string {
  return `teacherFeedback/data/courses/${courseId}/zoomUsers/${zoomUserId}`;
}
